import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { SEO } from "@/components/SEO";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setStatus("error"); return; }
    if (attempt > 8) { setStatus("pending"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setInfo(data);
      if (data.payment_status === "paid") { setStatus("paid"); return; }
      if (["expired", "failed"].includes(data.payment_status)) { setStatus("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div data-testid="payment-success-page" className="min-h-[70vh] flex items-center justify-center px-6">
      <SEO title="Payment" description="Payment confirmation" />
      <div className="bg-taupe rounded-3xl p-12 max-w-lg w-full text-center">
        {status === "checking" && (
          <><Loader2 className="mx-auto text-champagne animate-spin" size={48} />
          <h1 className="font-serif text-3xl text-espresso mt-6">Confirming your payment…</h1>
          <p className="text-espresso/70 mt-3">Just a moment while we verify with our payment processor.</p></>
        )}
        {status === "paid" && (
          <><CheckCircle2 className="mx-auto text-forest" size={48} />
          <h1 className="font-serif text-3xl text-espresso mt-6">Thank you! Payment confirmed.</h1>
          <p className="text-espresso/70 mt-3">{info?.package_name ? `Your purchase of "${info.package_name}" is complete.` : "Your purchase is complete."} A receipt has been sent by Stripe to your email. We'll reach out to begin.</p>
          <Link to="/" className="inline-block mt-8 bg-espresso text-ivory rounded-full px-7 py-3 font-medium hover:bg-forest transition-colors" data-testid="success-home">Back to Home</Link></>
        )}
        {status === "pending" && (
          <><Loader2 className="mx-auto text-champagne" size={48} />
          <h1 className="font-serif text-3xl text-espresso mt-6">Payment is processing.</h1>
          <p className="text-espresso/70 mt-3">This is taking a little longer than usual. You'll receive a confirmation email once it completes.</p>
          <Link to="/" className="inline-block mt-8 bg-espresso text-ivory rounded-full px-7 py-3 font-medium hover:bg-forest transition-colors">Back to Home</Link></>
        )}
        {status === "error" && (
          <><XCircle className="mx-auto text-red-700" size={48} />
          <h1 className="font-serif text-3xl text-espresso mt-6">We couldn't confirm this payment.</h1>
          <p className="text-espresso/70 mt-3">If you were charged, please contact us and we'll sort it out right away.</p>
          <Link to="/pricing" className="inline-block mt-8 border border-espresso text-espresso rounded-full px-7 py-3 font-medium hover:bg-espresso hover:text-ivory transition-colors">Back to Pricing</Link></>
        )}
      </div>
    </div>
  );
}
